/**
 * HABITS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import {
  PlusIcon,
  FireIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  Squares2X2Icon,
  PencilIcon,
  TrashIcon,
  HeartIcon,
  BookOpenIcon,
  AcademicCapIcon,
  SparklesIcon,
  BeakerIcon,
  CakeIcon,
  CameraIcon,
  ChatBubbleLeftIcon,
  CodeBracketIcon,
  CogIcon,
  CpuChipIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  FilmIcon,
  GlobeAltIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SunIcon,
  TrophyIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { Habit } from '../types/habit';
import { format } from 'date-fns';
import HabitForm from '../components/forms/HabitForm';
import { Spinner, KPICard } from '../components/ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Screen, Panel, Button } from '../design/primitives';

// Icon mapping for habits
const habitIconMap: { [key: string]: React.ComponentType<{ style?: React.CSSProperties; className?: string }> } = {
  CheckCircleIcon,
  FireIcon,
  HeartIcon,
  BookOpenIcon,
  AcademicCapIcon,
  SparklesIcon,
  BeakerIcon,
  CakeIcon,
  CameraIcon,
  ChatBubbleLeftIcon,
  CodeBracketIcon,
  CogIcon,
  CpuChipIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  FilmIcon,
  GlobeAltIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SunIcon,
  TrophyIcon,
  WrenchScrewdriverIcon,
  Squares2X2Icon,
  ClockIcon
};

const HabitsPage: React.FC = () => {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedHabit, setSelectedHabit] = useState<Habit | undefined>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const habitsRes = await apiClient.get('/api/habits/');
      setHabits(habitsRes.data);
    } catch (error) {
      console.error('Error fetching habits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleMarkCompleted = async (habitId: number) => {
    setUpdating(habitId);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      await apiClient.post(`/api/habits/${habitId}/mark_completed/`, { date: today });
      await fetchData();
    } catch (error) {
      console.error('Error marking habit:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleMarkMissed = async (habitId: number) => {
    setUpdating(habitId);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      await apiClient.post(`/api/habits/${habitId}/mark_not_completed/`, { date: today });
      await fetchData();
    } catch (error) {
      console.error('Error marking habit:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleOpenCreateForm = () => {
    setSelectedHabit(undefined);
    setIsEditMode(false);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (habit: Habit) => {
    setSelectedHabit(habit);
    setIsEditMode(true);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedHabit(undefined);
    setIsEditMode(false);
  };

  const handleHabitSaved = () => {
    fetchData();
    handleCloseForm();
  };

  const handleDelete = async (habitId: number) => {
    if (!window.confirm('Are you sure you want to delete this habit? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/api/habits/${habitId}/`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting habit:', error);
      alert('Failed to delete habit. Please try again.');
    }
  };

  const getStreakColor = (streak: number) => {
    if (streak >= 7) return COLORS.accent.default;
    return COLORS.text.secondary;
  };

  const getCompletionRateColor = (rate: number) => {
    if (rate >= 80) return COLORS.status.success;
    if (rate >= 60) return COLORS.status.warning;
    if (rate >= 40) return COLORS.status.warning;
    return COLORS.status.error;
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <Spinner size="lg" />
          <p
            style={{
              marginTop: SPACING[16],
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary
            }}
          >
            Loading your habits...
          </p>
        </div>
      </div>
    );
  }

  const activeHabits = habits.filter(h => h.is_active);
  const completedToday = activeHabits.filter(h => h.today_status === 'completed');
  const pendingHabits = activeHabits.filter(h => h.today_status === 'not_tracked');

  return (
    <Screen>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
        {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <h1
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0
          }}
        >
          Habits
        </h1>
        <Button
          variant="primary"
          onClick={handleOpenCreateForm}
          icon={<PlusIcon style={{ width: '20px', height: '20px' }} />}
        >
          New Habit
        </Button>
      </div>

      {/* Statistics Dashboard */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: SPACING[16]
        }}
      >
        <KPICard
          title="Total Habits"
          value={activeHabits.length}
          icon={Squares2X2Icon}
        />

        <KPICard
          title="Completed Today"
          value={completedToday.length}
          icon={CheckCircleIcon}
        />

        <KPICard
          title="Pending"
          value={pendingHabits.length}
          icon={ClockIcon}
        />

        <KPICard
          title="Today's Progress"
          value={`${activeHabits.length > 0 ? Math.round((completedToday.length / activeHabits.length) * 100) : 0}%`}
          icon={ChartBarIcon}
        />
      </div>

      {/* Habits Grid */}
      <Panel>
        {activeHabits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: `${SPACING[48]} 0` }}>
            <Squares2X2Icon
              style={{
                width: '64px',
                height: '64px',
                color: COLORS.text.tertiary,
                margin: '0 auto',
                marginBottom: SPACING[16]
              }}
            />
            <p
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0
              }}
            >
              No habits yet. Create your first one!
            </p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: SPACING[16]
            }}
          >
            {activeHabits.map((habit) => (
              <div
                key={habit.id}
                style={{
                  backgroundColor: COLORS.bg.panel,
                  border: `1px solid ${COLORS.border.default}`,
                  borderTop: `4px solid ${habit.color}`,
                  borderRadius: RADIUS,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = COLORS.border.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = COLORS.border.default;
                }}
              >
                <div style={{ padding: SPACING[24] }}>
                  {/* Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: SPACING[16]
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          borderRadius: RADIUS
                        }}
                      >
                        {(() => {
                          const IconComponent = habitIconMap[habit.icon] || Squares2X2Icon;
                          return <IconComponent style={{ width: '24px', height: '24px', color: COLORS.accent.default }} />;
                        })()}
                      </div>
                      <div>
                        <h3
                          style={{
                            fontFamily: TYPOGRAPHY.headings.fontFamily,
                            fontSize: TYPOGRAPHY.headings.sizes.h3,
                            fontWeight: TYPOGRAPHY.headings.weight,
                            color: COLORS.text.primary,
                            margin: 0,
                            marginBottom: SPACING[4]
                          }}
                        >
                          {habit.name}
                        </h3>
                        <p
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            color: COLORS.text.secondary,
                            margin: 0,
                            textTransform: 'capitalize'
                          }}
                        >
                          {habit.frequency}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: SPACING[4] }}>
                      <button
                        onClick={() => handleOpenEditForm(habit)}
                        title="Edit habit"
                        style={{
                          padding: SPACING[8],
                          color: COLORS.text.secondary,
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: RADIUS,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = COLORS.accent.default;
                          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = COLORS.text.secondary;
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <PencilIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(habit.id)}
                        title="Delete habit"
                        style={{
                          padding: SPACING[8],
                          color: COLORS.text.secondary,
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: RADIUS,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = COLORS.status.error;
                          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = COLORS.text.secondary;
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  {habit.description && (
                    <p
                      style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.secondary,
                        margin: 0,
                        marginBottom: SPACING[16],
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {habit.description}
                    </p>
                  )}

                  {/* Statistics */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: SPACING[12],
                      marginBottom: SPACING[16]
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: COLORS.bg.base,
                        borderRadius: RADIUS,
                        padding: SPACING[12]
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                        <FireIcon
                          style={{
                            width: '20px',
                            height: '20px',
                            color: getStreakColor(habit.current_streak)
                          }}
                        />
                        <div>
                          <p
                            style={{
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.tiny,
                              color: COLORS.text.secondary,
                              margin: 0
                            }}
                          >
                            Streak
                          </p>
                          <p
                            style={{
                              fontFamily: TYPOGRAPHY.headings.fontFamily,
                              fontSize: TYPOGRAPHY.headings.sizes.h3,
                              fontWeight: '700',
                              color: COLORS.text.primary,
                              margin: 0
                            }}
                          >
                            {habit.current_streak} days
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        backgroundColor: COLORS.bg.base,
                        borderRadius: RADIUS,
                        padding: SPACING[12]
                      }}
                    >
                      <div>
                        <p
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            color: COLORS.text.secondary,
                            margin: 0,
                            marginBottom: SPACING[4]
                          }}
                        >
                          7-Day Rate
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                          <div
                            style={{
                              flex: 1,
                              backgroundColor: COLORS.border.default,
                              borderRadius: '9999px',
                              height: '8px',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${habit.completion_rate_7d}%`,
                                backgroundColor: getCompletionRateColor(habit.completion_rate_7d),
                                borderRadius: '9999px',
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.small,
                              fontWeight: '700',
                              color: COLORS.text.primary,
                              minWidth: '35px',
                              textAlign: 'right'
                            }}
                          >
                            {Math.round(habit.completion_rate_7d)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: SPACING[8] }}>
                    <button
                      onClick={() => handleMarkCompleted(habit.id)}
                      disabled={updating === habit.id || habit.today_status === 'completed'}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: SPACING[8],
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        borderRadius: RADIUS,
                        cursor: habit.today_status === 'completed' || updating === habit.id ? 'default' : 'pointer',
                        opacity: updating === habit.id ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        border: 'none',
                        backgroundColor: habit.today_status === 'completed'
                          ? 'rgba(16, 185, 129, 0.15)'
                          : COLORS.bg.base,
                        color: habit.today_status === 'completed'
                          ? COLORS.status.success
                          : COLORS.text.primary
                      }}
                      onMouseEnter={(e) => {
                        if (habit.today_status !== 'completed' && updating !== habit.id) {
                          e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.15)';
                          e.currentTarget.style.color = COLORS.status.success;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (habit.today_status !== 'completed' && updating !== habit.id) {
                          e.currentTarget.style.backgroundColor = COLORS.bg.base;
                          e.currentTarget.style.color = COLORS.text.primary;
                        }
                      }}
                    >
                      <CheckCircleIcon style={{ width: '20px', height: '20px' }} />
                      <span>
                        {habit.today_status === 'completed' ? 'Completed!' : 'Complete'}
                      </span>
                    </button>

                    <button
                      onClick={() => handleMarkMissed(habit.id)}
                      disabled={updating === habit.id || habit.today_status === 'missed'}
                      style={{
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        borderRadius: RADIUS,
                        cursor: habit.today_status === 'missed' || updating === habit.id ? 'default' : 'pointer',
                        opacity: updating === habit.id ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        border: 'none',
                        backgroundColor: habit.today_status === 'missed'
                          ? 'rgba(239, 68, 68, 0.15)'
                          : COLORS.bg.base,
                        color: habit.today_status === 'missed'
                          ? COLORS.status.error
                          : COLORS.text.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        if (habit.today_status !== 'missed' && updating !== habit.id) {
                          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
                          e.currentTarget.style.color = COLORS.status.error;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (habit.today_status !== 'missed' && updating !== habit.id) {
                          e.currentTarget.style.backgroundColor = COLORS.bg.base;
                          e.currentTarget.style.color = COLORS.text.primary;
                        }
                      }}
                    >
                      <XCircleIcon style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

        {/* Habit Form Modal */}
        <HabitForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onHabitCreated={handleHabitSaved}
          initialHabit={selectedHabit}
          isEditMode={isEditMode}
        />
      </div>
    </Screen>
  );
};

export default HabitsPage;
